import { PreferenceClient } from '../client/preference.client';
import { SelectedCustomFeatureContext } from '../common/selection.menu/types';
import { checkForValidLabelString, sanitizeInput } from '../common/util';
import { ContextManager } from '../context/manager';
import { Formatting } from '../data/formatting';
import { PageInfo } from '../page/types';
import {
  DataType,
  Entity,
  EventElementCategory,
  EventElementType,
  EventName,
  FilterDefinition,
  FilterSections,
  FiltersValues,
  FilterType,
  FormatHint,
  Params,
  PreferenceName,
} from '../types';
import {
  CustomCategoricalClass,
  CustomClass,
  CustomNumericalClass,
  CustomPriceClass,
  SharePermissionsBody,
  UserAccess,
} from '../types/content/custom.types';
import constants from './constants';
const { MIN_CUSTOM_CLASS_TIER_VALUE, MAX_CUSTOM_CLASS_TIER_VALUE, LABEL_EXISTS } = constants;

interface SelectedFilter {
  [x: string]: { entity: Entity[]; definition: FilterDefinition };
}
interface FeatureFilters {
  [key: string]: {
    maxFeatureLimit: number;
    selectedFilters: SelectedFilter[];
  };
}

export const adjustDateRangeFilter = (type: FilterType, filterValue: string) => {
  if (type === FilterType.dateRange) {
    if (filterValue.startsWith('-')) {
      return filterValue.replace(/^-/, 'before ');
    } else if (filterValue.endsWith('-')) {
      return 'after ' + filterValue.replace(/-$/, '');
    }
  }
  // If no hyphen is found, return the original string
  return filterValue;
};

export const adjustNumericFilter = (type: FilterType, filterValue: string) => {
  if (type === FilterType.numericRange) {
    if (filterValue.startsWith('-')) {
      return filterValue.replace(/^-/, 'to ');
    } else if (filterValue.endsWith('-')) {
      return 'from ' + filterValue.replace(/-$/, '');
    }
  }
  // If no hyphen is found, return the original string
  return filterValue;
};

const initFeatureFilters = (sections: FilterSections[]): FeatureFilters => {
  const result = {};
  sections.forEach(({ label, maxFeatureLimit }) => {
    result[label] = { maxFeatureLimit, selectedFilters: [] };
  });
  return result;
};

export const pickFeatureFilters = (allFilters: FiltersValues, sections: FilterSections[]): FeatureFilters => {
  const result = initFeatureFilters(sections);

  for (const filter of Object.keys(allFilters)) {
    const filterValues = allFilters[filter];
    if (!filterValues.length) continue;

    const definitionEntry = findFilterDefinition(sections, filter);
    if (!definitionEntry) continue;

    const { label, definition } = definitionEntry;
    result[label].selectedFilters.push({
      [filter]: { entity: filterValues, definition },
    });
  }

  return result;
};

const findFilterDefinition = (
  sections: FilterSections[],
  filter: string
): { label: string; definition: FilterDefinition } | null => {
  for (const { label, grouped, ungrouped } of sections) {
    const groupedDef = grouped?.flatMap(g => g.definitions).find(d => d.name === filter);
    if (groupedDef) return { label, definition: groupedDef };

    const ungroupedDef = ungrouped?.definitions.find(d => d.name === filter);
    if (ungroupedDef) return { label, definition: ungroupedDef };
  }
  return null;
};

const getFilterSpesifications = (selectedFilters: SelectedFilter) => {
  let canRound = true;
  let roundingValue;
  let roundingCurrency;
  Object.values(selectedFilters).forEach(({ entity, definition }) => {
    if (!canRound) {
      return;
    }
    entity.forEach(({ value }) => {
      if (!value) {
        return;
      }
      const [min, max] = value.split('-').map(Number);
      if (
        definition.roundTo &&
        (failsRoundingCriteria(min, definition.roundTo) || failsRoundingCriteria(max, definition.roundTo))
      ) {
        roundingValue = definition.roundTo;
        canRound = false;
        roundingCurrency = definition.currency;
      }
    });
  });
  return { canRound, roundingValue, roundingCurrency };
};

export const getRoundingError = (selectedSections: FeatureFilters) => {
  let canFilterRound = true;
  let filterRoundingValue;
  let filterRoundingCurrency;
  Object.keys(selectedSections).forEach(label => {
    selectedSections[label].selectedFilters.forEach(filter => {
      if (!canFilterRound) {
        return;
      }
      const { canRound, roundingValue, roundingCurrency } = getFilterSpesifications(filter);
      canFilterRound = canRound;
      filterRoundingValue = roundingValue;
      filterRoundingCurrency = roundingCurrency;
    });
  });
  if (!canFilterRound) {
    const formatted = Formatting.format(filterRoundingValue, {
      dataType: DataType.Integer,
      formatHint: FormatHint.Money,
      currency: filterRoundingCurrency,
    });
    return `Invalid price range entered. Enter lower and upper price range that can be divided by ${formatted}.`;
  }
};
const getExceedingFilter = ({ maxFeatureLimit, selectedFilters }) => selectedFilters.length > maxFeatureLimit;

export const getMaxFeatureLimit = (featureFilters: FeatureFilters): number => {
  return Object.values(featureFilters).find(getExceedingFilter).maxFeatureLimit;
};

export const getIsConfidentialityError = (featureFilters: FeatureFilters): string => {
  if (Object.values(featureFilters).some(getExceedingFilter)) {
    const maxFeatureLimit = getMaxFeatureLimit(featureFilters);
    const CONFIDENTIALITY_ERROR = `Feature selection is limited to ${maxFeatureLimit} categories.`;
    return CONFIDENTIALITY_ERROR;
  }
};

export const getError = (featureFilters: FeatureFilters): string => {
  return getIsConfidentialityError(featureFilters) || getRoundingError(featureFilters);
};

export const getJoinedLabel = (values: Entity[]): string => {
  return (values || [])
    .map(e => e.label)
    .sort((a, b) => a.localeCompare(b))
    .join(', ');
};

type EventDetails = [
  EventName,
  {
    eventType: EventElementType;
    eventElementCategory: EventElementCategory;
    elementName: string;
    globalFilterApplied: string;
  },
];
export const getFilterEventDetails = (elementName: string, globalFilterApplied: string = 'Yes'): EventDetails => {
  return [
    EventName.PageElementClicked,
    {
      eventType: EventElementType.Button,
      eventElementCategory: EventElementCategory.SaveFilter,
      elementName,
      globalFilterApplied,
    },
  ];
};

export const failsRoundingCriteria = (value: number, roundTo: number | undefined | null) => {
  // gymnastics to avoid floating point rounding issues
  // roundTo is limited to 1 decimal place, so 100 is a safe value to multiply by
  //A small threshold (1e-10) is used to compare the result of the modulo operation instead of strict equality to 0. This accounts for small inaccuracies caused by floating-point arithmetic.
  //Performing modulo operations before scaling to avoid floating-point precision errors when dealing with large numbers.
  return roundTo !== undefined && roundTo !== null && Math.abs((value * (1 / roundTo)) % 1) > 1e-10;
};

/**
 * Retrieves and check if the input is within the range of MIN_CUSTOM_CLASS_TIER_VALUE and MAX_CUSTOM_CLASS_TIER_VALUE.
 * the min or max is return if it does not lie within the range, else the original value is returned
 * @param value Input value
 * @returns Number
 */
export const getValidInputWithinRange = (value: number): number => {
  if (value > MAX_CUSTOM_CLASS_TIER_VALUE) {
    return MAX_CUSTOM_CLASS_TIER_VALUE;
  } else if (value < MIN_CUSTOM_CLASS_TIER_VALUE) {
    return MIN_CUSTOM_CLASS_TIER_VALUE;
  }
  return value;
};

/**
 * Retrieves and check if the input is within the given range, minAllowedValue and maxAllowedValue.
 * the min or max is return if it does not lie within the range, else the original value is returned
 * @param value Input value
 * @param defaultFeatureClass feature classes with min and max value
 * @param classToRender current feature class
 * @param fieldType type of field if max or min
 * @returns Number
 */
export const getValidFeaturesInputWithinRange = (
  value: number,
  defaultFeatureClass: CustomNumericalClass,
  classToRender: CustomNumericalClass,
  fieldType: 'min' | 'max'
): number => {
  if (fieldType === 'min') {
    // Allow values above max but enforce the minimum limit
    if (value < defaultFeatureClass.min || value > defaultFeatureClass.max || value === classToRender.max) {
      return defaultFeatureClass.min;
    }
  } else if (fieldType === 'max') {
    // Allow values below min but enforce the maximum limit
    if (value > defaultFeatureClass.max || value < defaultFeatureClass.min || value === classToRender.min) {
      return defaultFeatureClass.max;
    } else {
      return value;
    }
  }
  // If within valid range, return the value
  return value;
};

/**
 * Function to determine rounded number input
 * @param input number input
 * @param prevMax number input
 * @param price price class info
 * @returns number
 */
export const roundInput = (
  input: number,
  prevMax: number | null,
  nextMax: number | null,
  price
): { roundedValue: number } => {
  const roundTo = Number(price.roundTo);
  // we aren't using !input as this would return true if input was 0
  if (input === undefined || input === null) return;
  //if input is less than default rounding value then return default round value
  if (input < roundTo) {
    input = roundTo;
  }

  const normalizedInput = input / roundTo;

  const nearestMultiple = Number((Math.round(normalizedInput) * roundTo).toFixed(2));
  // Rule 1: If `nearestMultiple` is below `prevMax`, sync to `prevMax` and adjust
  if (prevMax && nearestMultiple <= prevMax) {
    let adjustedPrevMax;
    const calculatedValue = Math.ceil(normalizedInput) * roundTo;
    const roundingOffset = calculatedValue === nearestMultiple ? roundTo : 0;

    if (nearestMultiple === prevMax) {
      adjustedPrevMax = calculatedValue + roundingOffset;
    } else {
      adjustedPrevMax = nearestMultiple;
    }

    adjustedPrevMax = Number(adjustedPrevMax.toFixed(2));

    return {
      roundedValue: adjustedPrevMax,
    };
  }

  // Rule 2: If `nearestMultiple` is equal to `nextMax`, round down and update
  if (nextMax && nearestMultiple == nextMax) {
    const adjustedNextMin = Number((Math.floor(normalizedInput) * roundTo).toFixed(2));
    return {
      roundedValue: adjustedNextMin, // Ensure rounded value stays within bounds
    };
  }

  // Default fallback for unhandled cases
  return {
    roundedValue: nearestMultiple,
  };
};

/**
 * Retrieve, process and return the price classes from the component settings
 * @param componentSettings - the component settings from PageSettingsManager
 * @param defaultPriceClass - the default price class to use if the component settings does not have any value
 * @returns - the price classes to use on loading the page
 */
export const getPriceClassesFromSettings = (componentSettings: Params, defaultPriceClass: CustomPriceClass) => {
  const priceClasses = componentSettings?.customPriceTiers;
  if (!priceClasses || !priceClasses.length) {
    return [defaultPriceClass];
  }

  const priceClassesArray = priceClasses[0].split('|').map((priceClass, index, list) => {
    const [strMin, strLabel] = priceClass.split(':');
    return {
      ...defaultPriceClass,
      min: Number(strMin),
      label: strLabel ?? '',
      max: index === list.length - 1 ? null : Number(list[index + 1].split(':')[0]),
    };
  });
  return priceClassesArray;
};

/**
 * Function to format the price classes to a single string for the params
 * @returns string
 */
export const formatPricesForParams = (priceClasses: CustomPriceClass[]) => {
  const startingZero = priceClasses.length === 0 ? '0' : '';
  const subsequentPriceClasses = priceClasses.map(price => `${price.min}:${price.label ?? ''}`).join('|');
  return startingZero.concat(subsequentPriceClasses);
};

/**
 * Function to check if the user is the creator of the preference
 * @param createdBy creator userId
 * @param userId current userId
 * @returns boolean
 */
export const isViewer = (createdBy: string, userId: string) => createdBy !== userId;

/**
 * Validates the custom class details, the name must be unique and not empty
 * @param filterName The unique name of the filter/class
 * @param filterDescription The description of the filter/class
 * @param isNameExistedFn The function to check if the name already exists
 * @returns true/false
 */
export const validateSaveCustomClassDetails = (
  filterName: string,
  filterDescription: string,
  isNameExistedFn: (name: string) => boolean
): boolean => {
  // Validate filterName is not empty and not only spaces
  if (filterName.trim().length === 0) {
    return false;
  }
  // checks if filterDescription does not contain spaces
  if (filterDescription && filterDescription.trim().length === 0) {
    return false;
  }
  // checks if filterName is unique
  if (isNameExistedFn(filterName)) {
    return false;
  }
  // All validations passed
  return true;
};

export const cleanString = (str: string): string => {
  str = str.trim();
  str = str.replace(/\s+/g, ' ');
  return str;
};

// Recursive function to filter hierarchy based on selected parents
export const filterByHierarchy = (
  hierarchyName: string,
  hierarchyLevel: string,
  hierarchyParent: string,
  options: any[],
  filteredOptionsMap: Record<string, any[]> = {},
  values: FiltersValues
) => {
  const level = parseInt(hierarchyLevel);
  if (level === 1) return options; // Return all options for the first hierarchy level

  const parentKey = `${hierarchyName}_${level - 1}`;
  const currentKey = `${hierarchyName}_${level}`;

  // Determine selected parents
  const selectedParents = values[parentKey]?.map(v => v.label) ?? filteredOptionsMap[parentKey] ?? [];

  // Filter options only if at least one parent is selected
  const filteredOptions =
    selectedParents.length > 0 ? options.filter(option => selectedParents.includes(option[hierarchyParent])) : options; // Keep all options if no parent is explicitly selected

  // Reset invalid selections in the current level
  if (values[currentKey]) {
    values[currentKey] = values[currentKey].filter(v => filteredOptions.some(option => option.label === v.label));
  }

  // Update filtered options map
  filteredOptionsMap[currentKey] = filteredOptions.map(opt => opt.label);

  return filteredOptions;
};

/**
 * Function to check if label is duplicate
 * @param customClasses list of price classes to validate the input againts
 * @returns boolean
 */
export const hasDuplicateLabels = (customClasses: CustomClass[]): boolean => {
  const labels = customClasses.map(customClass => customClass.label).filter(label => !!label);
  return new Set(labels).size !== labels.length;
};

/**
 * Function to check if the class is a duplicate
 * @param inputClass the input class
 * @param customClasses list of price classes to validate the input againts
 * @returns boolean
 */
export const isClassDuplicate = (inputClass: CustomNumericalClass, customClasses: CustomNumericalClass[]): boolean => {
  const existing = customClasses.filter(x => x.min === inputClass.min && x.max === inputClass.max);
  return existing.length > 1;
};

/**
 * Function to check if label is invalid
 * @param customClassesToValidate list of feature or price classes to validate the input againts
 * @returns boolean
 */
export const isLabelValid = (input: string): boolean => {
  const sanitizedInput = sanitizeInput(input); //prevent from saving empty space
  return !checkForValidLabelString(sanitizedInput);
};

/**
 * Function to validate the label input
 * @param customClassesToValidate list of feature or price classes to validate
 */
export const validateLabelInput = (customClassesToValidate: CustomClass[]): boolean => {
  if (hasDuplicateLabels(customClassesToValidate)) {
    return false;
  }
  const areLabelsValid = customClassesToValidate.every(customClass => {
    // if one of the label input is invalid, then disable the apply button and return
    return isLabelValid(customClass.label);
  });

  return areLabelsValid;
};

export const validateNumberInput = (featureClassesToValidate: CustomNumericalClass[]) => {
  //if at least one feature class is invalid then we will set the setIsDisabled to true
  const hasInvalidClass = featureClassesToValidate.some(featureClass => isRangeInvalid(featureClass));
  //if at least one feature class is duplicate then we will invalidate the input
  const hasDuplicateClass = featureClassesToValidate.some(featureClass =>
    isClassDuplicate(featureClass, featureClassesToValidate)
  );
  const allClassesValid = !hasInvalidClass && !hasDuplicateClass;
  return allClassesValid;
};

/**
 * Function to check if feature class is invalid
 * @param currentFeatureClass the current feature class
 * @returns boolean
 */
export const isRangeInvalid = (currentFeatureClass: CustomNumericalClass): boolean => {
  if (!currentFeatureClass.max) {
    return false;
  }

  const isInvalid: boolean = currentFeatureClass.min > currentFeatureClass.max;

  return isInvalid;
};
export type FormatFeatureClassInput = {
  selectedFeatureName: string;
  selectedFeatureType: FilterType;
  segmentClasses: CustomNumericalClass[] | CustomCategoricalClass[];
  excludeOthers?: boolean;
};
/**
 * Function to format the custom feature segment
 * @param selectedFeature filter definition of selected base feature
 * @param featureClasses
 * @returns
 */
export const formatFeatureClassForParams = ({
  selectedFeatureName,
  selectedFeatureType,
  segmentClasses,
  excludeOthers,
}: FormatFeatureClassInput): string => {
  const name = `name:${selectedFeatureName}|`;
  const subsequentClass =
    segmentClasses
      .map(feature =>
        selectedFeatureType === FilterType.numericRange
          ? `${(feature as CustomNumericalClass).min}:${feature.label ?? ''}`
          : `${(feature as CustomCategoricalClass).selectedOptions}:${feature.label}`
      )
      .join('|') + (selectedFeatureType !== FilterType.numericRange && !excludeOthers ? '|Others' : '');
  return name + subsequentClass;
};

/**
 * Function to adjust the price classes after deletion
 * @param index the index of the price class to be deleted
 * @param classes the list of price classes to be updated
 * @param classMaxValue the maximum value for class
 * @returns void
 */
export const adjustClassesAfterDeletion = (
  index: number,
  classes: CustomPriceClass[] | CustomNumericalClass[],
  classMaxValue?: number
): CustomPriceClass[] | CustomNumericalClass[] => {
  const updatedClasses = [...classes]; // Make a shallow copy to avoid direct mutation
  const toRemove = classes[index];

  if (index === 0) {
    updatedClasses[index + 1] = { ...classes[index + 1], min: 0 };
    return updatedClasses;
  }

  if (index === classes.length - 1) {
    updatedClasses[index - 1] = { ...classes[index - 1], max: classMaxValue ?? null };
    return updatedClasses;
  }

  updatedClasses[index + 1] = { ...classes[index + 1], min: toRemove.min };
  updatedClasses[index - 1] = { ...classes[index - 1], max: toRemove.min };

  return updatedClasses;
};

/**
 * Get the currently shared users of the saved custom segment
 * @param preferenceId the preferenceId of the saved custom segment
 * @returns the list of shared users
 */
export const getSharedUsers = (preferenceId: string, preferenceName: PreferenceName): Promise<UserAccess[]> => {
  return PreferenceClient.getInstance().getPermissions(preferenceName, preferenceId, true);
};

/**
 * Share the saved price class to the user
 * @param preferenceId preferenceId of the saved custom segment
 * @param permissionsBody list of users with their access level to be passed in as body
 */
export const shareToUser = async (
  preferenceId: string,
  preferenceName: PreferenceName,
  permissionsBody: SharePermissionsBody[]
) => {
  await PreferenceClient.getInstance().addPermissions(preferenceName, preferenceId, permissionsBody);
};

export const updateLabelErrors = <T extends CustomClass>(classes: T[]): T[] => {
  const labelCount: Record<string, number> = {};

  // Count occurrences of each label
  for (const cls of classes) {
    if (!cls.label) continue;
    labelCount[cls.label] = (labelCount[cls.label] || 0) + 1;
  }

  // Update labelError for each class
  return classes.map(cls => {
    const error = checkForValidLabelString(cls.label);
    return {
      ...cls,
      labelError: error || (labelCount[cls.label] > 1 ? LABEL_EXISTS : undefined),
    };
  }) as T[];
};

interface SelectedFeatureDetails {
  isDefaultState: boolean;
  feature?: FilterDefinition;
  retainedFeatureClasses?: CustomNumericalClass[] | CustomCategoricalClass[];
}

export const getFeatureClassesFromSettings = (
  componentSettings: Params,
  contextCustomFeature: SelectedCustomFeatureContext,
  filterDefinitions: FilterDefinition[],
  page: PageInfo,
  originMenu?: string
): SelectedFeatureDetails => {
  const selectedBaseFeature = contextCustomFeature?.value;
  const featureClasses = componentSettings?.customFeatureTiers;

  if (!selectedBaseFeature || !featureClasses || featureClasses.length === 0) {
    return { isDefaultState: true };
  }

  let relevantTier: string | undefined;
  if (originMenu) {
    relevantTier = featureClasses.find(tier => tier.startsWith(`${originMenu}:`));
    if (relevantTier) {
      // Remove the menu prefix to get the actual tier data
      relevantTier = relevantTier.substring(relevantTier.indexOf(':') + 1);
    }
  }

  if (!relevantTier) {
    return { isDefaultState: true };
  }

  let selectedFeatureClass = filterDefinitions.find(filterDefinition => relevantTier.includes(filterDefinition.name));

  if (!selectedFeatureClass) {
    ContextManager.getInstance().updateCustomFeatureValues({
      customFeature: {
        [page.name]: {
          [originMenu]: {
            selectedFeature: undefined,
          },
        },
      },
    });
    return { isDefaultState: true };
  }

  selectedFeatureClass = { ...selectedFeatureClass, value: selectedFeatureClass.name };

  const isNumericRange = selectedFeatureClass.type === FilterType.numericRange;

  const featureClassesArray: CustomNumericalClass[] | CustomCategoricalClass[] = relevantTier
    .split('|')
    .splice(1)
    .filter(featureClass => !featureClass.includes('Others'))
    .map((featureClass, index, list) => {
      const [strMin, strLabel] = featureClass.split(':');

      if (isNumericRange) {
        return {
          min: Number(strMin),
          label: strLabel ?? '',
          max: index === list.length - 1 ? null : Number(list[index + 1].split(':')[0]),
        };
      }

      return {
        label: strLabel ?? '',
        selectedOptions: strMin.split(','),
        options: selectedFeatureClass.options,
      };
    });

  return { isDefaultState: false, feature: selectedFeatureClass, retainedFeatureClasses: featureClassesArray };
};
